/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { EcmrRole } from '@app/core/enums/ecmr/EcmrRole';
import { EcmrShare } from '@app/shared/models/ecmr/EcmrShare';
import { EcmrShareResponse } from '@app/shared/models/ecmr/EcmrShareResponse';
import { SealMetadata } from '@app/shared/models/ecmr/SealMetadata';
import { EcmrUrlResolverService } from './ecmr-url-resolver.service';

@Injectable({
    providedIn: 'root',
})
export class ExternalUserService {
    constructor(
        private http: HttpClient,
        handler: HttpBackend,
        private ecmrUrlResolver: EcmrUrlResolverService
    ) {
        this.http = new HttpClient(handler);
    }

    getEcmrWithTan(ecmrId: string, userToken: string, tan: string) {
        return this.http.get<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}`,
            { params: { tan: tan, userToken: userToken } }
        );
    }

    isTanValid(ecmrId: string, userToken: string, tan: string) {
        return this.http.get<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/is-tan-valid`,
            { params: { ecmrId: ecmrId, tan: tan, userToken: userToken } }
        );
    }

    getEcmrRolesForUser(
        ecmrId: string,
        userToken: string,
        tan: string
    ): Observable<EcmrRole[]> {
        return this.http.get<EcmrRole[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr-role`,
            { params: { ecmrId: ecmrId, tan: tan, userToken: userToken } }
        );
    }

    updateEcmr(ecmr: Ecmr, userToken: string, tan: string) {
        return this.http.put<Ecmr>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr`,
            ecmr,
            { params: { tan: tan, userToken: userToken } }
        );
    }

    sealEcmr(ecmrId: string, userToken: string, tan: string) {
        return this.http.post<void>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}/seal`,
            null,
            { params: { tan: tan, userToken: userToken } }
        );
    }

    getSealMetadata(ecmrId: string, userToken: string, tan: string) {
        return this.http.get<SealMetadata[]>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}/seal-metadata`,
            { params: { tan: tan, userToken: userToken } }
        );
    }

    getShareToken(
        ecmrId: string,
        role: EcmrRole,
        userToken: string,
        tan: string
    ) {
        return this.http.get(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}/share-token`,
            {
                params: { ecmrRole: role, tan: tan, userToken: userToken },
                responseType: 'text',
            }
        );
    }

    downloadPdf(
        ecmrId: string,
        userToken: string,
        tan: string,
        language: string
    ) {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/pdf');
        return this.http.get(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}/pdf`,
            {
                headers,
                responseType: 'blob',
                observe: 'response',
                params: { tan: tan, userToken: userToken, language: language },
            }
        );
    }

    shareEcmr(
        ecmrShare: EcmrShare,
        ecmrId: string,
        userToken: string,
        tan: string
    ) {
        return this.http.patch<EcmrShareResponse>(
            `${this.ecmrUrlResolver.getEcmrUrl()}/anonymous/ecmr/${ecmrId}/share`,
            ecmrShare,
            { params: { tan: tan, userToken: userToken } }
        );
    }
}
